import {
  ClassReferenceParent as ParentClassReference,
  APIReference_apiReference_ClassReference_callback as ClassReferenceCallbacks,
  APIReference_apiReference_ClassReference_event as ClassReferenceEvents,
  APIReference_apiReference_ClassReference_method as ClassReferenceMethods,
  APIReference_apiReference_ClassReference_property as ClassReferenceProperties,
  APIReference_apiReference_GlobalReference as GlobalReference,
  APIReference_apiReference_GlobalReference_properties as GlobalReferenceProperties,
  APIReference_apiReference_GlobalReference_functions as GlobalReferenceFunctions,
  APIReference_apiReference_EnumReference as EnumReference,
  APIReference_apiReference_DatatypeReference as DatatypeReference,
  APIReference_apiReference_LibraryReference as LibraryReference,
  APIReference_apiReference_LibraryReference_function as LibraryReferenceFunctions,
  APIReference_apiReference_LibraryReference_property as LibraryReferenceProperties,
  APIReference_apiReference_DatatypeReference_function as DatatypeReferenceFunctions,
  APIReference_apiReference_DatatypeReference_property as DatatypeReferenceProperties,
  APIReference_apiReference_DatatypeReference_constructor as DatatypeReferenceConstructors,
  APIReference_apiReference_DatatypeReference_methods as DatatypeReferenceMethods,
  APIReference_apiReference_EnumReference_items as EnumReferenceItem,
  APIReference_apiReference_DatatypeReference_mathOperations as DatatypeReferenceMathOperations,
  APIReference_apiReference_ClassReference_method_parameter as APIReferenceParameters,
  APIClassReference,
} from '@modules/client';
import {
  ClassReferenceParents_classReferenceParents_method as ParentClassReferenceMethods,
  ClassReferenceParents_classReferenceParents_event as ParentClassReferenceEvents,
  ClassReferenceParents_classReferenceParents_callback as ParentClassReferenceCallbacks,
  ClassReferenceParents_classReferenceParents_property as ParentClassReferenceProperties,
} from '@modules/client/types/ClassReferenceParents';
import getReferenceTooltipDataMapKey from '@modules/utilities/pageBuild/getReferenceTooltipDataMapKey';
import { useTranslation } from '@rbx/intl';

export type APIReferenceProperties = ClassReferenceProperties | ParentClassReferenceProperties;
export type APIReferenceMethods = ClassReferenceMethods | ParentClassReferenceMethods;
export type APIReferenceEvents = ClassReferenceEvents | ParentClassReferenceEvents;
export type APIReferenceCallbacks = ClassReferenceCallbacks | ParentClassReferenceCallbacks;

export type APIReferencePropertyList = APIReferenceProperties[] | null;
export type APIReferenceMethodList = APIReferenceMethods[] | null;
export type APIReferenceEventList = APIReferenceEvents[] | null;
export type APIReferenceCallbackList = APIReferenceCallbacks[] | null;

// TODO: depluralize
export type CharacteristicsRowTypes =
  | APIReferenceProperties
  | APIReferenceMethods
  | APIReferenceEvents
  | APIReferenceCallbacks;

export type GlobalReferencePropertyList = GlobalReferenceProperties[] | null;
export type GlobalReferenceFunctionList = GlobalReferenceFunctions[] | null;
export type GlobalCharacteristicRowTypes = GlobalReferenceProperties | GlobalReferenceFunctions;

export type LibraryReferencePropertyList = LibraryReferenceProperties[] | null;
export type LibraryReferenceFunctionList = LibraryReferenceFunctions[] | null;
export type LibraryCharacteristicRowTypes = LibraryReferenceProperties | LibraryReferenceFunctions;

export type DatatypeReferencePropertyList = DatatypeReferenceProperties[] | null;
export type DatatypeReferenceConstructorList = DatatypeReferenceConstructors[] | null;
export type DatatypeReferenceMethodList = DatatypeReferenceMethods[] | null;
export type DatatypeReferenceFunctionList = DatatypeReferenceFunctions[] | null;
export type DatatypeReferenceMathOperationList = DatatypeReferenceMathOperations[] | null;
// TODO: clean up
export type DatatypeCharacteristicRowTypes =
  | DatatypeReferenceProperties
  | DatatypeReferenceConstructors
  | DatatypeReferenceMethods
  | DatatypeReferenceFunctions
  | DatatypeReferenceMathOperations;

export type AllClassReferences = APIClassReference | ParentClassReference;
type ClassReference = APIClassReference;

export type Reference =
  | GlobalReference
  | LibraryReference
  | DatatypeReference
  | EnumReference
  | ClassReference;

export type TypeAliasDatumItem = {
  // single type
  path?: string; // URL path to reference page, if any (none for e.g. 'void')
  canonicalLabel: string;
  tooltipProps?: ReferenceTypeData['tooltip'] & {
    // no tooltip if tooltipProps is undefined
    title: string;
    subtitle?: string;
    description?: string;
  };
};

export type MultiOrTypesCompoundTypeAliasDatum = {
  multipleOrTypes: TypeAliasDatumItem[];
};

export type HybridTypeAliasDatum = {
  outerType: TypeAliasDatumItem;
  innerTypes: TypeAliasDatumItem[];
};

export type TypeAliasDatum =
  | TypeAliasDatumItem
  | MultiOrTypesCompoundTypeAliasDatum
  | HybridTypeAliasDatum;

export const isTypeAliasDatumItem = (candidate: unknown): candidate is TypeAliasDatumItem =>
  !!candidate && typeof candidate === 'object' && 'canonicalLabel' in candidate;

export const isMultiOrTypesCompoundTypeAliasDatum = (
  candidate: unknown,
): candidate is MultiOrTypesCompoundTypeAliasDatum =>
  !!candidate && typeof candidate === 'object' && 'multipleOrTypes' in candidate;

export const isHybridTypeAliasDatum = (candidate: unknown): candidate is HybridTypeAliasDatum =>
  !!candidate &&
  typeof candidate === 'object' &&
  'outerType' in candidate &&
  'innerTypes' in candidate;

export const isTypeAliasDatum = (candidate: unknown): candidate is TypeAliasDatum =>
  !!candidate &&
  typeof candidate === 'object' &&
  ('canonicalLabel' in candidate || 'compoundType' in candidate);

export interface ReferenceTypeData {
  aliases: string[]; // how the type is possibly written in reference yaml file content
  canonical: string; // the canonical type name, displayed in reference page frontend
  tooltip?: {
    descriptionTranslKey?: string; // translation key for a fixed tooltip description,
    // likely stored in the translation namespace
    // `CreatorDocumentation.APIReference`
    descriptionTranslKeyMap?: Parameters<ReturnType<typeof useTranslation>['translate']>[1]; // tags for the translation key
  };
}

export type ReferenceTypeByPath = Map<string, ReferenceTypeData>;

export type ReferenceTooltipDataMapByTypeAlias = Map<
  ReturnType<typeof getReferenceTooltipDataMapKey>,
  TypeAliasDatumItem
>;

export type NextjsSerializableReferenceTypeDataByTypeAlias = Record<string, TypeAliasDatumItem>;

export enum ReferencePage {
  LuauReferencePage = 'LuauReferencePage', // maps to a /luau/ article
  ApiReferencePage = 'ApiReferencePage', // maps to an engine/cloud api page
  NullReferencePage = 'NullReferencePage', // maps to no reference page
}

export type {
  ClassReference,
  GlobalReference,
  LibraryReference,
  DatatypeReference,
  EnumReference,
  EnumReferenceItem,
  ParentClassReference,
  ClassReferenceProperties,
  ClassReferenceCallbacks,
  ClassReferenceEvents,
  ClassReferenceMethods,
  GlobalReferenceProperties,
  GlobalReferenceFunctions,
  LibraryReferenceProperties,
  LibraryReferenceFunctions,
  DatatypeReferenceProperties,
  DatatypeReferenceMethods,
  DatatypeReferenceFunctions,
  DatatypeReferenceConstructors,
  DatatypeReferenceMathOperations,
  APIReferenceParameters,
};
